// Zustand store of the realtime state that is not server data (docs/plan/12-frontend.md §5:
// presence and typing never live in the query cache): the state of the connection, the presence
// of the people the server told us about, and the status the person chose for themselves.
import type { PresenceState, PresenceStatus } from '@batiflow/contracts';
import { createStore, type StoreApi } from 'zustand/vanilla';

/** `idle`: realtime is not running (not signed in, or switched off); `up` / `down`: connected or lost. */
export type RealtimeLink = 'idle' | 'up' | 'down';

export interface RealtimeStoreState {
  link: RealtimeLink;
  /** Contacts who are online, by user id. A person who is not listed is offline. */
  presence: Readonly<Record<string, PresenceState>>;
  /** Status messages of the contacts who are online (COMX-FE-05), by user id; none when empty. */
  messages: Readonly<Record<string, string>>;
  /** The person's own status message (null: none) and how many times they set it (to resend it). */
  ownMessage: string | null;
  messageRevision: number;
  /** The status the person picked (`available` by default). */
  chosen: PresenceStatus;
  /** True while "away" was set automatically after a period without input. */
  autoAway: boolean;
  /**
   * Who is typing, by channel: user id → the instant (ms) the signal lapses. The server relays the
   * signal at most once per 3 s and never stores it: a person stops "typing" when no new signal
   * comes before their expiry.
   */
  typing: Readonly<Record<string, Readonly<Record<string, number>>>>;

  setLink: (link: RealtimeLink) => void;
  /** `presence.snapshot`: replaces everything known about others. */
  applySnapshot: (
    items: readonly { userId: string; status: PresenceState; message?: string | null }[],
  ) => void;
  /** `presence.changed`: one person went online, changed status or went offline. */
  applyChange: (userId: string, status: PresenceState, message?: string | null) => void;
  choose: (status: PresenceStatus) => void;
  /** The person set (or cleared, with null) their own status message. */
  setOwnMessage: (message: string | null) => void;
  setAutoAway: (value: boolean) => void;
  /** `typing`: someone is typing in a channel until `expiresAt`. */
  applyTyping: (channelId: string, userId: string, expiresAt: number) => void;
  /** Forgets the signals that lapsed before `now` (and the channels left empty). */
  pruneTyping: (now: number) => void;
  /** Back to the initial state (sign-out). */
  reset: () => void;
}

export type RealtimeStore = StoreApi<RealtimeStoreState>;

export function createRealtimeStore(): RealtimeStore {
  return createStore<RealtimeStoreState>()((set) => ({
    link: 'idle',
    presence: {},
    messages: {},
    ownMessage: null,
    messageRevision: 0,
    chosen: 'available',
    autoAway: false,
    typing: {},
    setLink: (link) => {
      set({ link });
    },
    applySnapshot: (items) => {
      const presence: Record<string, PresenceState> = {};
      const messages: Record<string, string> = {};
      for (const item of items) {
        if (item.status === 'offline') continue;
        presence[item.userId] = item.status;
        if (item.message) messages[item.userId] = item.message;
      }
      set({ presence, messages });
    },
    applyChange: (userId, status, message = null) => {
      set((state) => {
        const others = Object.entries(state.presence).filter(([id]) => id !== userId);
        const messages = Object.entries(state.messages).filter(([id]) => id !== userId);
        if (status !== 'offline') {
          others.push([userId, status]);
          if (message) messages.push([userId, message]);
        }
        return { presence: Object.fromEntries(others), messages: Object.fromEntries(messages) };
      });
    },
    setOwnMessage: (message) => {
      set((state) => ({
        ownMessage: message === null || message === '' ? null : message,
        messageRevision: state.messageRevision + 1,
      }));
    },
    choose: (status) => {
      set({ chosen: status, autoAway: false });
    },
    setAutoAway: (value) => {
      set({ autoAway: value });
    },
    applyTyping: (channelId, userId, expiresAt) => {
      set((state) => ({
        typing: {
          ...state.typing,
          [channelId]: { ...state.typing[channelId], [userId]: expiresAt },
        },
      }));
    },
    pruneTyping: (now) => {
      set((state) => {
        const typing: Record<string, Record<string, number>> = {};
        let changed = false;
        for (const [channelId, people] of Object.entries(state.typing)) {
          const alive = Object.entries(people).filter(([, expiresAt]) => expiresAt > now);
          if (alive.length !== Object.keys(people).length) changed = true;
          if (alive.length > 0) typing[channelId] = Object.fromEntries(alive);
        }
        return changed ? { typing } : state;
      });
    },
    reset: () => {
      set({
        link: 'idle',
        presence: {},
        messages: {},
        ownMessage: null,
        messageRevision: 0,
        chosen: 'available',
        autoAway: false,
        typing: {},
      });
    },
  }));
}

/** The status the server should hold for this person: "away" while idle, otherwise their pick. */
export const selectEffectiveStatus = (state: RealtimeStoreState): PresenceStatus =>
  state.autoAway ? 'away' : state.chosen;

/** What to show on the person's own avatar; undefined until the connection has been up once. */
export function selectOwnPresence(state: RealtimeStoreState): PresenceState | undefined {
  if (state.link === 'idle') return undefined;
  return state.link === 'up' ? selectEffectiveStatus(state) : 'offline';
}

/** What to show on someone else's avatar; undefined when the connection cannot tell. */
export function selectPresenceOf(
  state: RealtimeStoreState,
  userId: string,
): PresenceState | undefined {
  if (state.link !== 'up') return undefined;
  return state.presence[userId] ?? 'offline';
}

/** The status message of someone else, when they are online and set one. */
export function selectMessageOf(state: RealtimeStoreState, userId: string): string | undefined {
  if (state.link !== 'up') return undefined;
  return state.messages[userId];
}

/** The person's own status message (shown while the connection is up). */
export const selectOwnMessage = (state: RealtimeStoreState): string | undefined =>
  state.link === 'up' ? (state.ownMessage ?? undefined) : undefined;
